import { useEffect, useRef } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { supabase } from '@/integrations/supabase/client';
import { Loader2 } from 'lucide-react';

// Xero bounces the user here after they authorise on the consent screen.
// We read ?code & ?state (or ?error) from the URL, hand them to the edge
// function to do the token exchange + organisation discovery, then redirect to
// the settings page where the Xero panel shows the success/error toast and
// (if needed) the organisation picker.
//
// This route MUST match the redirect URI registered with Xero. We send
// `redirect_origin` to the edge function so it can reconstruct that URI for
// the token exchange step.
export default function XeroCallback() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  // useEffect fires twice in React 18 strict mode dev. We only want one
  // request, since the auth code is single-use.
  const ranRef = useRef(false);

  useEffect(() => {
    if (ranRef.current) return;
    ranRef.current = true;

    const code = searchParams.get('code');
    const state = searchParams.get('state');
    const oauthError = searchParams.get('error');

    const goBack = (params: Record<string, string>) => {
      const qs = new URLSearchParams(params).toString();
      navigate(`/settings?${qs}`, { replace: true });
    };

    if (oauthError) {
      goBack({ xero: 'error', reason: oauthError });
      return;
    }
    if (!code || !state) {
      goBack({ xero: 'error', reason: 'missing_params' });
      return;
    }

    (async () => {
      try {
        const { data, error } = await supabase.functions.invoke('xero-oauth-callback', {
          body: { code, state, redirect_origin: window.location.origin },
        });
        if (error) {
          let reason = error.message ?? 'callback_failed';
          const ctx = (error as { context?: Response }).context;
          if (ctx && typeof ctx.json === 'function') {
            try {
              const errBody = (await ctx.json()) as { reason?: string; error?: string };
              reason = errBody.reason || errBody.error || reason;
            } catch {
              // body wasn't JSON — keep the wrapper message
            }
          }
          goBack({ xero: 'error', reason: String(reason).slice(0, 200) });
          return;
        }
        const result = data as { ok?: boolean; multiple_tenants?: boolean; reason?: string };
        if (!result?.ok) {
          goBack({ xero: 'error', reason: result?.reason ?? 'unknown' });
          return;
        }
        const params: Record<string, string> = { xero: 'connected' };
        if (result.multiple_tenants) params.multiple_tenants = '1';
        goBack(params);
      } catch (e) {
        goBack({ xero: 'error', reason: e instanceof Error ? e.message.slice(0, 200) : 'unknown' });
      }
    })();
  }, [searchParams, navigate]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="flex items-center gap-3 text-sm text-muted-foreground">
        <Loader2 className="h-5 w-5 animate-spin" />
        Connecting Xero…
      </div>
    </div>
  );
}
